/**
 * Client-side Firestore API for the Owner ↔ Steuerberater messaging platform
 * (M-MSG-2). Mirrors the owner/tenant-scoped access pattern of
 * `scannedReceiptsApi.ts`: writes set `userId = effectiveScopeUid`, reads carry
 * `where('userId','==', scopeUid)`, and cross-tenant access is denied both here
 * and by the Firestore Rules (architecture-messaging-platform.md §4).
 *
 * Scope resolution follows the same rule as the rest of the app: the non-hook
 * {@link getEffectiveScopeUid} getter is bit-identical to `auth.currentUser.uid`
 * while tenant-scoping is off (M11 inertness), and the mandate scope when an
 * advisor has a foreign tenant active.
 *
 * IMPORTANT — role handling: this module is NOT a React hook and therefore must
 * NOT read `activeRole` from `useAuth()`. The author identity/role is passed in
 * by the calling hook ({@link MessageAuthContext}); {@link useMessages} supplies
 * it from `useAuth().activeRole` / `currentUser.uid`.
 *
 * DB→FE mapping (§3.6): `createdAt` stays a `Timestamp`; `context.type` and
 * `authorRole` are runtime-narrowed on read and a document with an unknown enum
 * value is DISCARDED with a warn log (no silent fallback). `threadKey` is used
 * for grouping/display only — never for authorization.
 *
 * @module messagesApi
 */

import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  serverTimestamp,
  getCountFromServer,
  doc,
  getDoc,
  setDoc,
  Timestamp,
  Unsubscribe,
  QuerySnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { getEffectiveScopeUid } from '../utils/tenantScope';
import {
  Message,
  MessageContext,
  MessageContextType,
  MessageAuthorRole,
  ReceiptRefCollection,
  CreateMessageInput,
} from '../types/messagingTypes';
import logger from '../utils/logger';

const MESSAGES_COLLECTION = 'messages';
const MESSAGE_READS_COLLECTION = 'message_reads';

/** threadKey used by the global (nav) unread cursor (§3.3 v1). */
const GLOBAL_CURSOR_THREAD_KEY = '_global_';

/** Message text length bounds (§3.2 / §4.4 textLenValid). */
const TEXT_MIN_LEN = 1;
const TEXT_MAX_LEN = 4000;

const CONTEXT_TYPES: readonly MessageContextType[] = ['general', 'invoice', 'receipt'];
const AUTHOR_ROLES: readonly MessageAuthorRole[] = ['owner', 'advisor'];
const RECEIPT_REF_COLLECTIONS: readonly ReceiptRefCollection[] = [
  'received_invoices',
  'scanned_receipts',
  // Welle 1 Cockpit-Intelligenz (architecture-cockpit-intelligence.md, Compile-
  // Blast-Radius Delta A) — the StB-Cockpit "Nachricht an Mandant" composer and
  // the owner-side MessageContextButton on InboundDocumentDetail both attach a
  // receipt-scoped message to an inbound_documents/{docId} WORM-pipeline doc.
  'inbound_documents',
];

/**
 * Deterministic author identity/role for a create, resolved by the calling hook
 * from `useAuth()`. Passing this in (instead of reading a hook here) keeps the
 * API layer hook-free while still letting the hook enforce the `activeRole`
 * derivation centrally.
 */
export interface MessageAuthContext {
  /** == tenantId == ownerUid; the message's `userId`. From `effectiveScopeUid`. */
  scopeUid: string;
  /** == request.auth.uid; the message's `authorUid`. From `currentUser.uid`. */
  authorUid: string;
  /** `'advisor'` iff `activeRole==='advisor'`, else `'owner'`. */
  authorRole: MessageAuthorRole;
}

// ─── toThreadKey ──────────────────────────────────────────────────────────────

/**
 * Materializes the implicit thread key for a context (§3.1):
 * `${type}:${refId ?? '_'}`. `general` → `"general:_"`, `invoice/{id}` →
 * `"invoice:<id>"`, `receipt/{id}` → `"receipt:<id>"`.
 *
 * @param ctx - The message context.
 * @returns The deterministic, indexable thread key.
 */
export function toThreadKey(ctx: MessageContext): string {
  return `${ctx.type}:${ctx.refId ?? '_'}`;
}

// ─── Internal: validation ─────────────────────────────────────────────────────

/**
 * Validates a {@link CreateMessageInput} at the module boundary (fail-closed,
 * Golden Rule). Throws a descriptive Error on the first violation.
 */
function validateCreateInput(input: CreateMessageInput): void {
  const { context, refCollection, text } = input;

  if (!CONTEXT_TYPES.includes(context.type)) {
    throw new Error(`messagesApi.sendMessage: invalid context.type "${String(context.type)}".`);
  }

  if (context.type === 'general') {
    if (context.refId != null) {
      throw new Error('messagesApi.sendMessage: general context must not carry a refId.');
    }
    if (refCollection != null) {
      throw new Error('messagesApi.sendMessage: general context must not carry a refCollection.');
    }
  } else {
    if (typeof context.refId !== 'string' || context.refId.trim().length === 0) {
      throw new Error(
        `messagesApi.sendMessage: context.type "${context.type}" requires a non-empty refId.`,
      );
    }
  }

  if (context.type === 'receipt') {
    if (refCollection == null || !RECEIPT_REF_COLLECTIONS.includes(refCollection)) {
      throw new Error(
        'messagesApi.sendMessage: receipt context requires refCollection ' +
          "'received_invoices' | 'scanned_receipts' | 'inbound_documents'.",
      );
    }
  } else if (context.type === 'invoice' && refCollection != null) {
    throw new Error('messagesApi.sendMessage: invoice context must not carry a refCollection.');
  }

  const trimmed = text.trim();
  if (trimmed.length < TEXT_MIN_LEN || trimmed.length > TEXT_MAX_LEN) {
    throw new Error(
      `messagesApi.sendMessage: text must be ${TEXT_MIN_LEN}..${TEXT_MAX_LEN} non-blank ` +
        `characters (got ${trimmed.length}).`,
    );
  }
}

// ─── Internal: DB→FE mapping ──────────────────────────────────────────────────

/**
 * Maps a raw Firestore message document to the {@link Message} type, runtime-
 * narrowing the `context.type` and `authorRole` enums (§3.6). Returns `null` for
 * a malformed/unknown-enum document — the caller DISCARDS it (no silent
 * fallback); the discard is logged.
 *
 * @param id - Firestore doc id.
 * @param data - Raw document data.
 * @returns A typed Message, or null if the document is malformed.
 */
function mapDocToMessage(id: string, data: DocumentData): Message | null {
  const rawContext = data.context;
  if (typeof rawContext !== 'object' || rawContext === null) {
    logger.warn('messagesApi: discarding message with missing context', { id });
    return null;
  }

  const contextType = rawContext.type as unknown;
  if (typeof contextType !== 'string' || !CONTEXT_TYPES.includes(contextType as MessageContextType)) {
    logger.warn('messagesApi: discarding message with unknown context.type', { id, contextType });
    return null;
  }

  const authorRole = data.authorRole as unknown;
  if (typeof authorRole !== 'string' || !AUTHOR_ROLES.includes(authorRole as MessageAuthorRole)) {
    logger.warn('messagesApi: discarding message with unknown authorRole', { id, authorRole });
    return null;
  }

  if (!(data.createdAt instanceof Timestamp)) {
    // createdAt is briefly null between local optimistic write and server ack;
    // such rows carry no reliable ordering key → skip until materialized.
    logger.warn('messagesApi: discarding message without server createdAt', { id });
    return null;
  }

  if (typeof data.userId !== 'string' || typeof data.authorUid !== 'string' ||
      typeof data.text !== 'string' || typeof data.threadKey !== 'string') {
    logger.warn('messagesApi: discarding message with missing scalar fields', { id });
    return null;
  }

  const refCollectionRaw = data.refCollection as unknown;
  const refCollection: ReceiptRefCollection | null =
    typeof refCollectionRaw === 'string' &&
    RECEIPT_REF_COLLECTIONS.includes(refCollectionRaw as ReceiptRefCollection)
      ? (refCollectionRaw as ReceiptRefCollection)
      : null;

  const refId = typeof rawContext.refId === 'string' ? rawContext.refId : null;
  const deletedAt = data.deletedAt instanceof Timestamp ? data.deletedAt : null;

  return {
    id,
    userId: data.userId,
    context: { type: contextType as MessageContextType, refId },
    refCollection,
    threadKey: data.threadKey,
    authorUid: data.authorUid,
    authorRole: authorRole as MessageAuthorRole,
    text: data.text,
    createdAt: data.createdAt,
    deletedAt,
  };
}

// ─── Internal: cursor read ────────────────────────────────────────────────────

/** Deterministic message_reads doc id: `${uid}__${tenantId}__${threadKey}` (§3.3). */
function cursorDocId(readerUid: string, scopeUid: string, threadKey: string): string {
  return `${readerUid}__${scopeUid}__${threadKey}`;
}

/**
 * Reads the `lastReadAt` cursor for (reader, tenant, thread) via a deterministic
 * O(1) get. Returns null when no cursor exists (→ everything counts as unread —
 * fail-open for display, never for authorization).
 */
async function readCursorLastReadAt(
  readerUid: string,
  scopeUid: string,
  threadKey: string,
): Promise<Timestamp | null> {
  const ref = doc(db, MESSAGE_READS_COLLECTION, cursorDocId(readerUid, scopeUid, threadKey));
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    return null;
  }
  const lastReadAt = snap.data().lastReadAt;
  return lastReadAt instanceof Timestamp ? lastReadAt : null;
}

// ─── sendMessage ──────────────────────────────────────────────────────────────

/**
 * Creates a message. Sets `userId` (= scopeUid), `threadKey`, `authorUid`,
 * `authorRole` and `createdAt` (= serverTimestamp) DETERMINISTICALLY — the
 * caller only supplies `context`/`refCollection`/`text` ({@link CreateMessageInput}).
 * The author identity/role comes from {@link MessageAuthContext}, resolved by the
 * calling hook from `useAuth()` (this module never reads a hook).
 *
 * @param input - Context + optional refCollection + text.
 * @param authCtx - Deterministic author scope/uid/role from the hook.
 * @returns The new Firestore document id.
 * @throws {Error} If auth scope is missing, validation fails, or the write fails.
 */
export async function sendMessage(
  input: CreateMessageInput,
  authCtx: MessageAuthContext,
): Promise<string> {
  if (!authCtx.scopeUid || !authCtx.authorUid) {
    throw new Error('messagesApi.sendMessage: missing scopeUid/authorUid (not authenticated).');
  }

  validateCreateInput(input);

  const refId = input.context.refId ?? null;
  const refCollection: ReceiptRefCollection | null =
    input.context.type === 'receipt' ? (input.refCollection ?? null) : null;

  const docData = {
    userId: authCtx.scopeUid,
    context: { type: input.context.type, refId },
    refCollection,
    threadKey: toThreadKey(input.context),
    authorUid: authCtx.authorUid,
    authorRole: authCtx.authorRole,
    text: input.text.trim(),
    createdAt: serverTimestamp(),
  };

  try {
    const docRef = await addDoc(collection(db, MESSAGES_COLLECTION), docData);
    return docRef.id;
  } catch (err) {
    throw new Error(
      `messagesApi.sendMessage: write failed — ${err instanceof Error ? err.message : String(err)}`,
    );
  }
}

// ─── subscribeThread ──────────────────────────────────────────────────────────

/**
 * Subscribes to a thread's messages in real time, ordered by `createdAt` asc.
 * Scope is resolved via {@link getEffectiveScopeUid} (bit-identical to
 * `auth.currentUser.uid` while tenant-scoping is off).
 *
 * Malformed documents are discarded during mapping (§3.6). Snapshot errors are
 * surfaced via the optional `onError` callback (additive to the §9.1 signature —
 * `useMessages` needs an error channel; see changelog).
 *
 * @param ctx - The thread context.
 * @param cb - Receives the ordered, mapped message list on every snapshot.
 * @param onError - Optional handler for the onSnapshot error path.
 * @returns The Firestore unsubscribe function.
 */
export function subscribeThread(
  ctx: MessageContext,
  cb: (msgs: Message[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  const scopeUid = getEffectiveScopeUid();
  if (!scopeUid) {
    logger.warn('messagesApi.subscribeThread: no scope uid (not authenticated) — no-op.');
    return () => undefined;
  }

  const q = query(
    collection(db, MESSAGES_COLLECTION),
    where('userId', '==', scopeUid),
    where('threadKey', '==', toThreadKey(ctx)),
    orderBy('createdAt', 'asc'),
  );

  return onSnapshot(
    q,
    (snapshot: QuerySnapshot<DocumentData>) => {
      const msgs: Message[] = [];
      snapshot.forEach((d) => {
        const mapped = mapDocToMessage(d.id, d.data());
        if (mapped) {
          msgs.push(mapped);
        }
      });
      cb(msgs);
    },
    (err) => {
      logger.error('messagesApi.subscribeThread: snapshot error', err);
      if (onError) {
        onError(err instanceof Error ? err : new Error(String(err)));
      }
    },
  );
}

// ─── getThreadUnreadCount ─────────────────────────────────────────────────────

/**
 * Counts unread messages in a single thread via a `count()` aggregate (reads no
 * documents). Unread = `createdAt > lastReadAt`; when no cursor exists the whole
 * thread counts as unread. Uses the composite index `userId, threadKey, createdAt`.
 *
 * @param ctx - The thread context.
 * @param scopeUid - Tenant scope (message `userId`).
 * @param readerUid - The viewer's uid (cursor owner).
 * @returns The number of unread messages in the thread.
 * @throws {Error} If the aggregate query fails.
 */
export async function getThreadUnreadCount(
  ctx: MessageContext,
  scopeUid: string,
  readerUid: string,
): Promise<number> {
  if (!scopeUid || !readerUid) {
    return 0;
  }
  const threadKey = toThreadKey(ctx);

  try {
    const lastReadAt = await readCursorLastReadAt(readerUid, scopeUid, threadKey);
    const base = collection(db, MESSAGES_COLLECTION);
    const q = lastReadAt
      ? query(
          base,
          where('userId', '==', scopeUid),
          where('threadKey', '==', threadKey),
          where('createdAt', '>', lastReadAt),
        )
      : query(base, where('userId', '==', scopeUid), where('threadKey', '==', threadKey));

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (err) {
    throw new Error(
      `messagesApi.getThreadUnreadCount: count failed — ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}

// ─── getThreadMessageCount ────────────────────────────────────────────────────

/**
 * Counts ALL messages ever posted in a thread (no `lastReadAt` filter), via a
 * `count()` aggregate (reads no documents). Used to distinguish "a discussion
 * exists (read or unread)" from "no discussion yet" — {@link getThreadUnreadCount}
 * alone cannot express that, since a fully-read thread reports 0 there too.
 *
 * Reuses the composite index `userId, threadKey, createdAt` as a prefix query
 * (`userId` + `threadKey` equality only) — no new Firestore index required.
 *
 * Fails SAFE to 0 on error (never throws) — this is a display-only signal, not
 * an authorization check, matching the fail-soft contract of the other
 * `useThreadActivity`/badge helpers in this module.
 *
 * @param ctx - The thread context.
 * @param scopeUid - Tenant scope (message `userId`).
 * @returns The total number of messages ever posted in the thread, or 0 on error.
 */
export async function getThreadMessageCount(ctx: MessageContext, scopeUid: string): Promise<number> {
  if (!scopeUid) {
    return 0;
  }
  try {
    const q = query(
      collection(db, MESSAGES_COLLECTION),
      where('userId', '==', scopeUid),
      where('threadKey', '==', toThreadKey(ctx)),
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (err) {
    logger.warn(
      'messagesApi.getThreadMessageCount: count failed — failing safe to 0',
      err instanceof Error ? err : new Error(String(err)),
    );
    return 0;
  }
}

// ─── markThreadRead ───────────────────────────────────────────────────────────

/**
 * Upserts the reader's `message_reads` cursor for a thread to now
 * (`serverTimestamp()`), via `setDoc(..., {merge:true})` on the deterministic
 * cursor doc. Marks everything up to now as read.
 *
 * @param ctx - The thread context.
 * @param scopeUid - Tenant scope the thread belongs to.
 * @param readerUid - The viewer's uid (cursor owner).
 * @throws {Error} If auth ids are missing or the write fails.
 */
export async function markThreadRead(
  ctx: MessageContext,
  scopeUid: string,
  readerUid: string,
): Promise<void> {
  if (!scopeUid || !readerUid) {
    throw new Error('messagesApi.markThreadRead: missing scopeUid/readerUid (not authenticated).');
  }
  const threadKey = toThreadKey(ctx);
  const ref = doc(db, MESSAGE_READS_COLLECTION, cursorDocId(readerUid, scopeUid, threadKey));

  try {
    await setDoc(
      ref,
      { userId: readerUid, tenantId: scopeUid, threadKey, lastReadAt: serverTimestamp() },
      { merge: true },
    );
  } catch (err) {
    throw new Error(
      `messagesApi.markThreadRead: write failed — ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}

// ─── getGlobalUnreadCount ─────────────────────────────────────────────────────

/**
 * Global (nav-badge) unread count — §3.3 v1 variant: a SINGLE `count()` over all
 * tenant messages newer than the global cursor (`threadKey === '_global_'`), via
 * the composite index `userId, createdAt`.
 *
 * Own messages (`authorUid === readerUid`) are NOT excluded: Firestore forbids a
 * second inequality (`authorUid != me`) alongside `createdAt >` in one query, and
 * a `count()` aggregate returns no documents to filter client-side. The small
 * over-count is accepted (§3.5 anti-inequality note); the global cursor is
 * advanced on opening the messages area and after own sends, so own messages drop
 * out of the count quickly. See changelog for this deliberate trade-off.
 *
 * @param scopeUid - Tenant scope (message `userId`).
 * @param readerUid - The viewer's uid (global cursor owner).
 * @returns Approximate number of unread messages across the tenant.
 * @throws {Error} If the aggregate query fails.
 */
export async function getGlobalUnreadCount(
  scopeUid: string,
  readerUid: string,
): Promise<number> {
  if (!scopeUid || !readerUid) {
    return 0;
  }

  try {
    const lastReadAt = await readCursorLastReadAt(readerUid, scopeUid, GLOBAL_CURSOR_THREAD_KEY);
    const base = collection(db, MESSAGES_COLLECTION);
    const q = lastReadAt
      ? query(base, where('userId', '==', scopeUid), where('createdAt', '>', lastReadAt))
      : query(base, where('userId', '==', scopeUid));

    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (err) {
    throw new Error(
      `messagesApi.getGlobalUnreadCount: count failed — ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}

// ─── markGlobalRead ───────────────────────────────────────────────────────────

/**
 * Advances the reader's GLOBAL cursor (`threadKey === '_global_'`) to now, so the
 * nav badge clears when the messages area is opened and own sends stop counting.
 * Same deterministic-cursor mechanics as {@link markThreadRead}.
 *
 * @param scopeUid - Tenant scope the global cursor belongs to.
 * @param readerUid - The viewer's uid (cursor owner).
 * @throws {Error} If auth ids are missing or the write fails.
 */
export async function markGlobalRead(scopeUid: string, readerUid: string): Promise<void> {
  if (!scopeUid || !readerUid) {
    throw new Error('messagesApi.markGlobalRead: missing scopeUid/readerUid (not authenticated).');
  }
  const ref = doc(
    db,
    MESSAGE_READS_COLLECTION,
    cursorDocId(readerUid, scopeUid, GLOBAL_CURSOR_THREAD_KEY),
  );

  try {
    await setDoc(
      ref,
      {
        userId: readerUid,
        tenantId: scopeUid,
        threadKey: GLOBAL_CURSOR_THREAD_KEY,
        lastReadAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (err) {
    throw new Error(
      `messagesApi.markGlobalRead: write failed — ${
        err instanceof Error ? err.message : String(err)
      }`,
    );
  }
}
